import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Executive Session — persists role selection, presentation mode, watchlist,
 * recent pages and workspace preferences per user in localStorage.
 * Presentation-only: never modifies backend state, permissions or workflows.
 */
const PresentationCtx = createContext({
  presentation: false, setPresentation: () => {},
  role: "commissioner", setRole: () => {},
  watchlist: [], addWatch: () => {}, removeWatch: () => {}, isWatched: () => false,
  recent: [], pushRecent: () => {},
});

const key = (k) => `cad360.${k}`;

export const PresentationProvider = ({ children }) => {
  const [presentation, setPresentation] = useState(() => localStorage.getItem(key("presentation")) === "1");
  const [role, setRole] = useState(() => localStorage.getItem(key("role")) || "commissioner");
  const [watchlist, setWatchlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key("watchlist")) || "[]"); } catch { return []; }
  });
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key("recent")) || "[]"); } catch { return []; }
  });

  useEffect(() => { localStorage.setItem(key("presentation"), presentation ? "1" : "0"); }, [presentation]);
  useEffect(() => { localStorage.setItem(key("role"), role); }, [role]);
  useEffect(() => { localStorage.setItem(key("watchlist"), JSON.stringify(watchlist)); }, [watchlist]);
  useEffect(() => { localStorage.setItem(key("recent"), JSON.stringify(recent)); }, [recent]);

  const addWatch = useCallback((item) => {
    setWatchlist((cur) => {
      if (cur.find((w) => w.id === item.id && w.type === item.type)) return cur;
      return [{ ...item, watched_at: new Date().toISOString() }, ...cur].slice(0, 200);
    });
  }, []);
  const removeWatch = useCallback((item) => {
    setWatchlist((cur) => cur.filter((w) => !(w.id === item.id && w.type === item.type)));
  }, []);
  const isWatched = useCallback((item) => watchlist.some((w) => w.id === item.id && w.type === item.type), [watchlist]);
  const pushRecent = useCallback((entry) => {
    setRecent((cur) => {
      const dedup = cur.filter((r) => r.to !== entry.to);
      return [{ ...entry, at: new Date().toISOString() }, ...dedup].slice(0, 24);
    });
  }, []);

  return (
    <PresentationCtx.Provider value={{
      presentation, setPresentation,
      role, setRole,
      watchlist, addWatch, removeWatch, isWatched,
      recent, pushRecent,
    }}>
      {children}
    </PresentationCtx.Provider>
  );
};

export const usePresentation = () => useContext(PresentationCtx);
